// Jotai atoms for offline-recording typed patient links (APP-6501, FR-005).
//
// Offline, patient linking is create-by-typed-id only: the clinician types a
// patient identifier into the session-header patient field and it is persisted
// as `patient_link` on the local offline session at create time. This atom is
// the bridge between that field, the session list, and `useRecordingButton`
// (which reads it when firing `offline_recording_create`).

import { atom, getDefaultStore } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

type SafeStringStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const noopStorage: SafeStringStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const getSafeSessionStorage = (): SafeStringStorage => {
  if (typeof window === 'undefined') return noopStorage;

  try {
    const storage = window.sessionStorage;
    return {
      getItem: (key) => {
        try {
          return storage.getItem(key);
        } catch {
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          storage.setItem(key, value);
        } catch {
          // Ignore storage write failures in restricted WebViews.
        }
      },
      removeItem: (key) => {
        try {
          storage.removeItem(key);
        } catch {
          // Ignore storage delete failures in restricted WebViews.
        }
      },
    };
  } catch {
    return noopStorage;
  }
};

const createSafeSessionStorage = <T>() =>
  createJSONStorage<T>(() => getSafeSessionStorage());

export const OFFLINE_PATIENT_LINKS_STORAGE_KEY =
  'heidi.offlineRecording.patientLinksBySessionId';

/**
 * Patient identifiers typed into the patient field while offline, keyed by
 * session id. Empty string = no link typed.
 */
export const offlinePatientLinksBySessionIdAtom = atomWithStorage<Record<string, string>>(
  OFFLINE_PATIENT_LINKS_STORAGE_KEY,
  {},
  createSafeSessionStorage<Record<string, string>>(),
  { getOnInit: true }
);

/**
 * The patient identifier typed into the patient field for one session. Read by
 * `useRecordingButton` to populate `CreateOfflineRecordingArgs.patientLink` on
 * `offline_recording_create`, and by UI surfaces that need to reflect the local
 * draft before the backend has any patient data.
 */
export const offlinePatientLinkAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) => {
      if (!sessionId) return '';
      return get(offlinePatientLinksBySessionIdAtom)[sessionId] ?? '';
    },
    (get, set, value: string) => {
      if (!sessionId) return;

      const current = get(offlinePatientLinksBySessionIdAtom);
      if (value.length === 0) {
        if (!(sessionId in current)) return;
        const next = { ...current };
        delete next[sessionId];
        set(offlinePatientLinksBySessionIdAtom, next);
        return;
      }

      if (current[sessionId] === value) return;
      set(offlinePatientLinksBySessionIdAtom, {
        ...current,
        [sessionId]: value,
      });
    }
  )
);

type PatientLinkStore = Pick<ReturnType<typeof getDefaultStore>, 'set'>;

/**
 * Drop every typed patient identifier. Called from `clearLogoutStorage`.
 *
 * Two writes, because either one on its own is a half-fix:
 *
 * - The atom is emptied, because removing the storage row behind a live
 *   `atomWithStorage` leaves memory and storage disagreeing. Its only
 *   subscription is the `storage` window event, which does not fire in the tab
 *   that made the change, so the atom would go on serving the departing
 *   clinician's value and would re-persist it on the next write — it would not
 *   even heal on a later reload. This is why `setWorkspaceId(null)` sits beside
 *   the call site rather than a key removal alone.
 * - The storage row is removed, because emptying the atom writes `{}` back
 *   through the adapter rather than clearing the row.
 *
 * This key is deliberately NOT in `LOGOUT_KEYS`: that list removes from
 * localStorage only, and these drafts are in sessionStorage — which survives
 * the sign-out branches that DO navigate, so the exposure was never confined to
 * the paths that skip a reload.
 *
 * Left behind on a shared clinical workstation, the next clinician's session
 * header and `offline_recording_create` would carry patient identifiers typed
 * by the previous one, under the new account's token.
 *
 * The per-session atoms in `offlinePatientLinkAtomFamily` are derived from this
 * map and hold no value of their own, so emptying it empties them.
 *
 * The store is a parameter so a test can drive the same store this wrote to
 * while the module is evaluated against the real storage implementation — the
 * only way to prove the next write does not re-persist. Production takes the
 * default store.
 */
export const clearOfflinePatientLinks = (
  store: PatientLinkStore = getDefaultStore()
): void => {
  store.set(offlinePatientLinksBySessionIdAtom, {});
  getSafeSessionStorage().removeItem(OFFLINE_PATIENT_LINKS_STORAGE_KEY);
};
